import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { Sensor } from '@models';
import { SensorCalibrationHistory, SensorCalibrationHistoryNotes } from '@models/Sensor/sensorCalibrationHistory.model';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import { Charting } from 'app/shared/classes/charting';
import { CommonShared } from 'app/shared/classes/common';
import { SensorMapping } from 'app/shared/classes/sensorMapping';
import moment from 'moment';
import { ConfirmationService } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { UserGroup } from '../../../shared/models';

@Component({
  standalone: false,
  selector: 'sensor-calibration-history',
  templateUrl: './sensor-calibration-history.component.html',
  styleUrls: ['./sensor-calibration-history.component.scss']
})

export class SensorCalibrationHistoryComponent implements OnInit {
  
  @Input() sensor: Sensor;
  @Input() associatedUserGroups: UserGroup[];
  @Output() calibrationDeleted = new EventEmitter<void>();
  @Output() calibrationUpdated = new EventEmitter<void>();
  @Output() activeCalibrationChanged = new EventEmitter<{calibrationId: number, isActive: boolean, sensorReadingTypeId: number}>();

  common = new CommonShared();
  mapping = new SensorMapping();

  username: string = '';
  editingRow: SensorCalibrationHistory = null;
  editingUserNotes: string = '';
  editingStartDate: string = '';

  calibrationHistoryCols = [
    { field: 'sensorName', header: 'Sensor & ReadingType', width: '17%' },
    { field: 'templateName', header: 'Equ', width: '7%' },
    { field: 'parametersDisplay', header: 'Parameters', width: '20%' },
    { field: 'dateRange', header: 'Start & Modified Dates', width: '18%' },
    { field: 'modifiedByUserName', header: 'Modified By', width: '14%' },
    { field: 'notes', header: '', width: '4%' },
    { field: 'edit', header: '', width: '4%' },
    { field: 'delete', header: '', width: '4%' },
    { field: 'active', header: 'Active', width: '5%' },
  ];


  constructor(private toastr: ToastrService, public _sensorService: SensorService, public _userService: UserService, private confirmationService: ConfirmationService) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
  }


  ngOnChanges() {
  }


  getFormattedDate(date: Date): string {
    const dateFormatted = moment(date).local().format('YYYY-MM-DD HH:mm');
    return dateFormatted;
  }


  canDeleteHistory(calibration: SensorCalibrationHistory): boolean {
    // Only allow deletion if modified by non-API user and not active or if user is in the Admins group or the SensorCalibrationAdmin group
    const isAdmin = this.associatedUserGroups?.some(group => group.name === 'Admins' || group.name === 'SensorCalibrationAdmin');
    return calibration.modifiedByUserName?.toLowerCase() !== 'api' || (isAdmin && !calibration.isActive);
  }


  canEditHistory(calibration: SensorCalibrationHistory): boolean {
    const isAdmin = this.associatedUserGroups?.some(group => group.name === 'Admins' || group.name === 'SensorCalibrationAdmin');
    return calibration.modifiedByUserName?.toLowerCase() !== 'api' || isAdmin;
  }


  onEditSensorCalibration(calibration: SensorCalibrationHistory) {
    if (!this.canEditHistory(calibration)) {
      return;
    }
    this.editingRow = calibration;
    this.editingUserNotes = calibration.notesObj?.userNotes || '';
    this.editingStartDate = calibration.startDate ? moment(calibration.startDate).local().format('YYYY-MM-DDTHH:mm') : '';
  }


  cancelEdit() {
    this.editingRow = null;
    this.editingUserNotes = '';
    this.editingStartDate = '';
  }


  // hasDateOverlap(calibration: SensorCalibrationHistory, newStart: moment.Moment): boolean {
  //   const sameTypeCals = this.calibrationHistory.filter(
  //     c => c.sensorReadingTypeId === calibration.sensorReadingTypeId && c.id !== calibration.id
  //   );
  //   for (const cal of sameTypeCals) {
  //     const existingStart = moment(cal.startDate);
  //     if (newStart.isSame(existingStart)) {
  //       return true;
  //     }
  //   }
  //   return false;
  // }


  saveCalibrationEdit(calibration: SensorCalibrationHistory) {
    if (!this.editingStartDate) {
      this.toastr.warning('Start date is required');
      return;
    }

    const newStart = moment(this.editingStartDate);

    // if (this.hasDateOverlap(calibration, newStart)) {
    //   this.toastr.warning('Start date conflicts with an existing calibration history record for the same reading type');
    //   return;
    // }

    calibration.notesObj.userNotes = this.editingUserNotes;
    const updatedNotes = JSON.stringify(calibration.notesObj);

    const body = {
      startDate: newStart.utc().format(),
      notes: updatedNotes
    };

    this._sensorService.updateCalibrationHistory(calibration.id, body, this.username).subscribe(
      results => {
        if (results === 'success' || results == null) {
          this.toastr.success('Calibration history updated successfully');
          this.editingRow = null;
          this.editingUserNotes = '';
          this.editingStartDate = '';
          this.calibrationUpdated.emit();
        } else {
          this.toastr.error('Error: ' + results);
        }
      },
      err => {
        this.toastr.error('Error updating calibration history');
      });
  }

  cancelEditUserNotes() {
    this.editingRow = null;
    this.editingUserNotes = '';
  }

  saveUserNotes(calibration: SensorCalibrationHistory) {
    calibration.notesObj.userNotes = this.editingUserNotes;
    const updatedNotes = JSON.stringify(calibration.notesObj);
    calibration.notes = updatedNotes;
    this._sensorService.updateCalibrationNotes(calibration.id, updatedNotes).subscribe(
      results => {
        if (results == "success") {
          this.toastr.success('User notes updated successfully');
          this.editingRow = null;
        }
        else {
          this.toastr.error('Error: ' + results);
        }
      },
      err => {
        this.toastr.error('Error updating user notes');
      });
  }


  onDeleteCalibrationConfirm(calibration: SensorCalibrationHistory) {
    this.confirmationService.confirm({
      message: 'Confirm. Delete this calibration history record?',
      key: 'calHistDelete',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        this._sensorService.deleteCalibrationHistory(calibration.id, this.username).subscribe(
          results => {
            if (results === 'success' || results == null) {
              this.toastr.success('Calibration history deleted successfully');
              const index = this.sensor.sensorCalibrationHistory.indexOf(calibration);
              if (index > -1) {
                this.sensor.sensorCalibrationHistory.splice(index, 1);
              }
              this.calibrationDeleted.emit();
            } else {
              this.toastr.error('Error: ' + results);
            }
          },
          err => {
            this.toastr.error('Error deleting calibration history');
          });
      },
      reject: () => {
      }
    });
  }


  getSensorCalibrationTooltipForSensor(calibration: SensorCalibrationHistory): string {
    return `Grade: ${calibration.notesObj.grade}\nSN: ${calibration.notesObj.sensorSerialNumber}`;
  }


  onActiveChanged(calibration: SensorCalibrationHistory) {
    if (calibration.isActive) {
      // Uncheck other calibrations of the same reading type
      this.sensor.sensorCalibrationHistory
        .filter(c => c.sensorReadingTypeId === calibration.sensorReadingTypeId && c.id !== calibration.id)
        .forEach(c => {
          if (c.isActive) {
            c.isActive = false;
            this.activeCalibrationChanged.emit({ calibrationId: c.id, isActive: false, sensorReadingTypeId: c.sensorReadingTypeId });
          }
        });
    }
    this.activeCalibrationChanged.emit({ calibrationId: calibration.id, isActive: calibration.isActive, sensorReadingTypeId: calibration.sensorReadingTypeId });
  }


}
